'use client';

/**
 * The {@code <video>} element backing {@link HeroVideo}. Split into
 * its own client component so the hero section, gradients, and overlay
 * copy can stay server-rendered while only the video hydrates.
 *
 * <p>Browser policy:
 * <ul>
 *   <li>{@code muted}: required for autoplay without user gesture</li>
 *   <li>{@code playsInline}: prevents iOS full-screen takeover</li>
 *   <li>{@code preload="auto"}: this IS the hero, fetch immediately</li>
 *   <li>{@code poster}: rendered until enough video loads</li>
 * </ul>
 *
 * <p>Respects {@code prefers-reduced-motion} — when set, skips
 * autoplay and shows the poster only.
 */
import { useEffect, useRef, useState } from 'react';
import { pickEnum } from '../_shared/coerce';
import { resolveInsetStyle, type PaddingBox } from '../_shared/resolvePadding';
import { BlockRoot } from '../_shared/BlockRoot';

interface HeroVideoBackgroundProps {
  src: string;
  poster: string | undefined;
  /** Reports the video's real (width / height) once metadata loads, so
   *  the wrapping section can size itself from that instead of a fixed
   *  viewport-height value. */
  onRatio?: (ratio: number) => void;
}

export function HeroVideoBackground({ src, poster, onRatio }: HeroVideoBackgroundProps) {
  const ref = useRef<HTMLVideoElement>(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const v = ref.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function')
      p.catch(() => {
        /* autoplay blocked */
      });
  }, [src, reduceMotion]);

  const onLoadedMetadata = () => {
    const v = ref.current;
    if (!v || !v.videoWidth || !v.videoHeight) return;
    onRatio?.(v.videoWidth / v.videoHeight);
  };

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload="auto"
      onLoadedMetadata={onLoadedMetadata}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        zIndex: 0,
      }}
    />
  );
}

export interface HeroVideoBackgroundHeroProps {
  videoUrl?: string;
  posterImage?: string;
  height?: 'medium' | 'large' | 'full';
  /** 'full' (default) lets the section span edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
  radius?: 'none' | 'sm' | 'md' | 'lg';
  /** Space-token keys for padding between the section's true edge and
   *  the video — the section's own background shows through this gap,
   *  framing the video (most visible once `radius` is rounded). */
  paddingOuter?: PaddingBox;
  /** Space-token keys for a second padding ring, inside `paddingOuter`,
   *  before the video itself. */
  paddingInner?: PaddingBox;
}

/** Fallback (width / height) used before the video reports its real
 *  metadata — avoids a layout jump by assuming standard widescreen. */
const FALLBACK_RATIO = 16 / 9;

const RADIUS_MAP: Record<NonNullable<HeroVideoBackgroundHeroProps['radius']>, string> = {
  none: '0px',
  sm: 'var(--radius-sm, 6px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 16px)',
};

/**
 * Chrome-free hero block: just the looping background video, no title,
 * kicker, or CTA overlay. For a version with text + CTAs, use HeroVideo.
 */
export function HeroVideoBackgroundHero({
  videoUrl,
  posterImage,
  height,
  maxWidth = 'full',
  radius = 'none',
  paddingOuter,
  paddingInner,
}: HeroVideoBackgroundHeroProps) {
  const [ratio, setRatio] = useState<number | undefined>(undefined);

  if (!videoUrl) return null;

  const h = pickEnum<'medium' | 'large' | 'full'>(
    height,
    ['medium', 'large', 'full'] as const,
    'large',
  );

  // Upper bound only — actual height comes from the video's real aspect
  // ratio (measured on `loadedmetadata`, see HeroVideoBackground above),
  // so the section scales fluidly with width instead of being pinned to
  // a fixed vh value. `height` just caps how tall that's allowed to get.
  const maxHeightStyle =
    h === 'full' ? 'min(100vh, 900px)' : h === 'medium' ? 'min(60vh, 560px)' : 'min(80vh, 720px)';

  const outerInset = resolveInsetStyle(paddingOuter);
  const innerInset = resolveInsetStyle(paddingInner);

  return (
    <BlockRoot
      as="section"
      style={{
        position: 'relative',
        aspectRatio: ratio ?? FALLBACK_RATIO,
        maxHeight: maxHeightStyle,
        overflow: 'hidden',
        borderRadius: RADIUS_MAP[radius],
        background: 'var(--color-neutral-900, #0c0a09)',
        ...(maxWidth === 'theme'
          ? {
              width: '100%',
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              marginTop: 0,
              marginRight: 'auto',
              marginBottom: 'var(--space-8, 32px)',
              marginLeft: 'auto',
            }
          : {
              marginTop: 0,
              marginLeft: 'var(--page-bleed-margin, calc(50% - 50vw))',
              marginRight: 'var(--page-bleed-margin, calc(50% - 50vw))',
              marginBottom: 'var(--space-8, 32px)',
              width: 'var(--page-bleed-width, 100vw)',
              maxWidth: 'var(--page-bleed-width, 100vw)',
            }),
      }}
    >
      {/* CSS `padding` on this section would NOT inset an `inset:0` child —
       *  an absolutely positioned element's containing block is the
       *  ancestor's padding box, which already includes the padding area.
       *  So each ring below carries its own gap as explicit inset offsets
       *  (resolveInsetStyle), not `padding`, to actually be visible. */}
      <div style={{ position: 'absolute', inset: 0, ...outerInset }}>
        <div style={{ position: 'absolute', inset: 0, ...innerInset }}>
          <HeroVideoBackground src={videoUrl} poster={posterImage} onRatio={setRatio} />
        </div>
      </div>
    </BlockRoot>
  );
}
